import React, { useEffect, useState } from 'react';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { showPageLoading, hidePageLoading } from '../utils/pageLoading';

const Captures = () => {
  const [isBottomHtmlLoaded, setIsBottomHtmlLoaded] = useState(false);

  // Loading overlay (same as the scene's, spinner only) from mount until the
  // captures list is rendered and its visible thumbnails have loaded. Also
  // ended on unmount, on a final script-load failure, and after 20 s at most.
  const pageLoadingRef = React.useRef(false);
  const endPageLoading = React.useCallback(() => {
    if (!pageLoadingRef.current) return;
    pageLoadingRef.current = false;
    hidePageLoading();
  }, []);
  useEffect(() => {
    pageLoadingRef.current = true;
    showPageLoading();
    const safety = setTimeout(endPageLoading, 20000);
    // Leaving the page (in-app navigation): end captures.js's viewer session
    // (its window listeners and scale animation) — see captures.js's top.
    return () => {
      clearTimeout(safety); endPageLoading(); window.__disposeViewer?.();
      document.querySelectorAll('script[data-viewer-top]').forEach((el) => el.remove());
      if (window.__profileOutsideClick) {
        document.removeEventListener('click', window.__profileOutsideClick);
        window.__profileOutsideClick = null;
      }
      import('../captures/CaptureHandler').then((m) => m.disposeCaptures?.()).catch(() => {});
    };
  }, [endPageLoading]);

  // Load top HTML
  useEffect(() => {
    fetch('/html/captures-top.html')
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then((html) => {
        const container = document.getElementById('html-container');
        if (container) {
          container.innerHTML = '<base href="/">' + html;

          // Re-execute inline scripts (innerHTML doesn't run them)
          container.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            newScript.dataset.viewerTop = '1';
            document.body.appendChild(newScript);
          });

          // Inject model name
          const modelId = new URLSearchParams(window.location.search).get('modelId');
          if (modelId) {
            authFetch(`${API_URL}?getModel=true&id=${modelId}`)
              .then(res => res.json())
              .then(data => {
                const el = document.getElementById('model-name');
                if (el && data.name) el.textContent = data.name;
              })
              .catch(() => {});
          }
        }
      })
      .catch((err) => console.error('Error loading top HTML:', err));
  }, []);

  // Load bottom HTML and captures.js
  useEffect(() => {
    let retries = 0;
    const maxRetries = 3;
    const retryDelayMs = 300; // delay before retrying

    fetch('/html/captures-bottom.html')
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then((html) => {
        const container = document.getElementById('html-container2');
        if (!container) return;

        container.innerHTML = '<base href="/">' + html;
        setIsBottomHtmlLoaded(true);

        // Wait two animation frames to let DOM render fully
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const capturesScript = document.createElement('script');
            capturesScript.src = '/js/captures.js';
            capturesScript.dataset.viewerTop = '1'; // removed on leaving
            capturesScript.async = true;

            capturesScript.onload = async () => {
              try {
                // Wait for HTML + script before calling CaptureHandler
                const { default: captureHandler } = await import('../captures/CaptureHandler');
                captureHandler('capture-container').finally(endPageLoading);

                // Run scrollbar initialization (if defined)
                const waitForDom = () => {
                  const ready =
                    document.getElementById('scroll-container') &&
                    document.querySelectorAll('.menu-item').length > 0;

                  if (ready && window.initViewer) {
                    try {
                      window.initViewer(); // runs initialize2() in captures.js
                    } catch (err) {
                      console.error('Error running initViewer (captures):', err);

                      // Retry after initViewer error, with delay
                      if (retries < maxRetries) {
                        retries += 1;
                        console.warn(
                          `Retrying captures.js load after initViewer error (${retries}/${maxRetries})`
                        );

                        setTimeout(() => {
                          const newScript = document.createElement('script');
                          newScript.src = '/js/captures.js';
                          newScript.dataset.viewerTop = '1'; // removed on leaving
                          newScript.async = true;
                          newScript.onload = capturesScript.onload;
                          newScript.onerror = capturesScript.onerror;

                          document.body.appendChild(newScript);
                        }, retryDelayMs);
                      }
                    }
                  } else {
                    requestAnimationFrame(waitForDom);
                  }
                };

                requestAnimationFrame(waitForDom);
              } catch (err) {
                console.error('Error initializing CaptureHandler:', err);
                endPageLoading();
              }
            };

            capturesScript.onerror = (err) => {
              console.error('Error loading captures.js:', err);
              if (retries < maxRetries) {
                retries += 1;
                console.warn(
                  `Retrying captures.js load after load error (${retries}/${maxRetries})`
                );

                // Retry after network/load error, with the same delay
                setTimeout(() => {
                  const newScript = document.createElement('script');
                  newScript.src = '/js/captures.js';
                  newScript.dataset.viewerTop = '1'; // removed on leaving
                  newScript.async = true;
                  newScript.onload = capturesScript.onload;
                  newScript.onerror = capturesScript.onerror;

                  document.body.appendChild(newScript);
                }, retryDelayMs);
              } else {
                endPageLoading(); // gave up — don't leave the overlay up
              }
            };

            document.body.appendChild(capturesScript);
          });
        });
      })
      .catch((err) => console.error('Error loading bottom HTML:', err));
  }, []);

  return (
    <div className="page-container">
      {/* Top */}
      {/* Above the grid (its favorite stars / outlines are z-index 10): the
          settings dropdown lives inside this header and can't rise above
          it, so on a tie the later grid drew over it. Same 100 as Projects. */}
      <div id="html-container" style={{ position: 'sticky', top: 0, zIndex: 100 }} />

      {/* Middle container */}
      <div
        id="capture-container"
        style={{ width: '100%', height: '100%', flexGrow: 1, display: 'block' }}
      />

      {/* Bottom */}
      <div id="html-container2" style={{ position: 'sticky', bottom: 0, zIndex: 10 }} />
    </div>
  );
};

export default Captures;
